import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../utils/api';
import {
  dailyCareLogsApi,
  residentCarePlanApi,
  DailyCareCategory,
  DailyCareSubItem,
  ResidentCarePlanItem,
  CarePlanItemInput
} from '../api/dailyCareLogs';

type Resident = { id: number; name: string; age: number; diagnosis: string; photo?: string | null };

type Draft = Record<number, { selected: boolean; supportNote: string; frequency: string }>;

type Template = { id: number; name: string; description?: string };

const FREQUENCY_OPTIONS = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'as_needed', label: 'As needed' }
];

const ResidentCarePlanEditor: React.FC = () => {
  const { residentId: residentIdParam } = useParams<{ residentId: string }>();
  const residentId = Number(residentIdParam);
  const navigate = useNavigate();

  const [resident, setResident] = useState<Resident | null>(null);
  const [categories, setCategories] = useState<DailyCareCategory[]>([]);
  const [draft, setDraft] = useState<Draft>({});
  const [expanded, setExpanded] = useState<Record<number, boolean>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [lastEditedInfo, setLastEditedInfo] = useState<{ updatedAt: string; editedByName: string } | null>(null);

  // Template modal state
  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [applyingTemplate, setApplyingTemplate] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [residentRes, cats, planItems] = await Promise.all([
        api.get(`/residents/${residentId}`),
        dailyCareLogsApi.getCategories(),
        residentCarePlanApi.list(residentId)
      ]);
      setResident(residentRes.data.data || residentRes.data);
      setCategories(cats);

      // Determine last-edited info from plan items
      if (planItems.length > 0) {
        const sorted = [...planItems]
          .filter((p: any) => p.updatedAt)
          .sort((a: any, b: any) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
        if (sorted.length > 0) {
          const latest: any = sorted[0];
          setLastEditedInfo({
            updatedAt: latest.updatedAt,
            editedByName: latest.assignedBy?.name || 'Unknown'
          });
        } else {
          setLastEditedInfo(null);
        }
      } else {
        setLastEditedInfo(null);
      }

      const planBySubId = new Map<number, ResidentCarePlanItem>(planItems.map(p => [p.subItemId, p]));
      const initial: Draft = {};
      const initialExpanded: Record<number, boolean> = {};
      for (const c of cats) {
        let anySelected = false;
        for (const s of c.subItems) {
          const existing = planBySubId.get(s.id);
          const selected = !!existing;
          if (selected) anySelected = true;
          initial[s.id] = {
            selected,
            supportNote: existing?.supportNote || '',
            frequency: existing?.frequency || 'daily'
          };
        }
        initialExpanded[c.id] = anySelected || c.displayOrder <= 2;
      }
      setDraft(initial);
      setExpanded(initialExpanded);
    } catch (e: any) {
      setError(e.response?.data?.message || 'Failed to load care plan');
    } finally {
      setLoading(false);
    }
  }, [residentId]);

  useEffect(() => {
    if (!Number.isFinite(residentId)) return;
    load();
  }, [load, residentId]);

  const toggleSub = (sub: DailyCareSubItem) => {
    setDraft(prev => ({
      ...prev,
      [sub.id]: {
        selected: !(prev[sub.id]?.selected),
        supportNote: prev[sub.id]?.supportNote || '',
        frequency: prev[sub.id]?.frequency || 'daily'
      }
    }));
  };

  const updateField = (subId: number, patch: Partial<{ supportNote: string; frequency: string }>) => {
    setDraft(prev => ({ ...prev, [subId]: { ...prev[subId], ...patch } }));
  };

  const selectedCount = useMemo(() => Object.values(draft).filter(d => d.selected).length, [draft]);

  const categoryStats = (cat: DailyCareCategory) => {
    const total = cat.subItems.length;
    const picked = cat.subItems.filter(s => draft[s.id]?.selected).length;
    return { total, picked };
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const items: CarePlanItemInput[] = Object.entries(draft)
        .filter(([, v]) => v.selected)
        .map(([sid, v]) => ({
          subItemId: Number(sid),
          supportNote: v.supportNote.trim() || null,
          frequency: v.frequency
        }));
      await residentCarePlanApi.saveBulk(residentId, items);
      setToast('Care plan saved');
      setTimeout(() => setToast(null), 2500);
    } catch (e: any) {
      setError(e.response?.data?.message || 'Failed to save care plan');
    } finally {
      setSaving(false);
    }
  };

  const openTemplateModal = async () => {
    setTemplateModalOpen(true);
    setSelectedTemplateId(null);
    setLoadingTemplates(true);
    try {
      const res = await api.get('/support-plan-templates');
      setTemplates(res.data.data || res.data || []);
    } catch {
      setError('Failed to load templates');
    } finally {
      setLoadingTemplates(false);
    }
  };

  const applyTemplate = async () => {
    if (!selectedTemplateId) return;
    setApplyingTemplate(true);
    try {
      await api.post('/support-plan-templates/apply', { residentId, templateId: selectedTemplateId });
      setTemplateModalOpen(false);
      setToast('Template applied successfully');
      setTimeout(() => setToast(null), 2500);
      await load();
    } catch (e: any) {
      setError(e.response?.data?.message || 'Failed to apply template');
    } finally {
      setApplyingTemplate(false);
    }
  };

  if (!Number.isFinite(residentId)) {
    return <div className="text-red-600">Invalid resident id.</div>;
  }

  return (
    <div className="pb-24">
      <div className="flex items-center justify-between mb-6">
        <div>
          <button
            onClick={() => navigate('/residents')}
            className="text-blue-600 hover:text-blue-800 text-sm mb-1"
          >
            &larr; Back to residents
          </button>
          <h1 className="text-2xl font-bold text-gray-900">
            Care Plan {resident ? `— ${resident.name}` : ''}
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            Tick the items this resident needs help with. Add a short support note so carers know <em>how</em> to deliver each task.
          </p>
          {lastEditedInfo && (
            <p className="text-xs text-gray-500 mt-1">
              Last edited: {new Date(lastEditedInfo.updatedAt).toLocaleDateString()} by {lastEditedInfo.editedByName}
            </p>
          )}
        </div>
        <div className="text-right space-y-2">
          <div className="text-sm text-gray-500">{selectedCount} item{selectedCount === 1 ? '' : 's'} selected</div>
          <button
            onClick={openTemplateModal}
            className="px-3 py-1.5 bg-indigo-600 text-white rounded-md text-sm font-medium hover:bg-indigo-700"
          >
            Start from Template
          </button>
        </div>
      </div>

      {/* Template Modal */}
      {templateModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 max-h-[80vh] flex flex-col">
            <div className="p-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Start from Template</h2>
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 mt-2">
                This will add template tasks to this resident's plan. Existing tasks will NOT be removed.
              </p>
            </div>
            <div className="p-4 overflow-y-auto flex-1">
              {loadingTemplates && <p className="text-sm text-gray-500">Loading templates...</p>}
              {!loadingTemplates && templates.length === 0 && (
                <p className="text-sm text-gray-500">No templates available.</p>
              )}
              <ul className="space-y-2">
                {templates.map(t => (
                  <li key={t.id}>
                    <label className="flex items-start gap-3 p-3 rounded-md border border-gray-200 hover:border-indigo-300 cursor-pointer">
                      <input
                        type="radio"
                        name="template"
                        checked={selectedTemplateId === t.id}
                        onChange={() => setSelectedTemplateId(t.id)}
                        className="mt-0.5 h-4 w-4 text-indigo-600"
                      />
                      <div>
                        <div className="text-sm font-medium text-gray-900">{t.name}</div>
                        {t.description && (
                          <div className="text-xs text-gray-500 mt-0.5">{t.description}</div>
                        )}
                      </div>
                    </label>
                  </li>
                ))}
              </ul>
            </div>
            <div className="p-4 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => setTemplateModalOpen(false)}
                className="px-4 py-2 text-sm text-gray-700 hover:text-gray-900"
              >
                Cancel
              </button>
              <button
                onClick={applyTemplate}
                disabled={!selectedTemplateId || applyingTemplate}
                className="px-4 py-2 bg-indigo-600 text-white rounded-md text-sm font-semibold hover:bg-indigo-700 disabled:opacity-50"
              >
                {applyingTemplate ? 'Applying...' : 'Apply Template'}
              </button>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4 text-sm">{error}</div>
      )}
      {toast && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded mb-4 text-sm">{toast}</div>
      )}
      {loading && <p className="text-gray-500 text-sm">Loading…</p>}

      <div className="space-y-4">
        {categories.map(cat => {
          const { total, picked } = categoryStats(cat);
          const open = !!expanded[cat.id];
          return (
            <div key={cat.id} className="bg-white rounded-lg shadow">
              <button
                type="button"
                onClick={() => setExpanded(s => ({ ...s, [cat.id]: !s[cat.id] }))}
                className="w-full flex items-center justify-between p-4 border-b border-gray-200"
              >
                <div className="text-left">
                  <h2 className="font-semibold text-gray-900">{cat.name}</h2>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {picked} of {total} selected
                  </p>
                </div>
                <span className="text-gray-400">{open ? '▾' : '▸'}</span>
              </button>

              {open && (
                <ul className="divide-y divide-gray-200">
                  {cat.subItems.length === 0 && (
                    <li className="px-4 py-3 text-sm text-gray-500 italic">No sub-items in this category</li>
                  )}
                  {cat.subItems.map(s => {
                    const d = draft[s.id] || { selected: false, supportNote: '', frequency: 'daily' };
                    return (
                      <li key={s.id} className="px-4 py-3">
                        <label className="flex items-start gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={d.selected}
                            onChange={() => toggleSub(s)}
                            className="mt-1 h-4 w-4 text-blue-600 rounded"
                          />
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-gray-900">{s.name}</span>
                              <span className="ml-2 px-1.5 py-0.5 text-xs rounded bg-gray-100 text-gray-600">
                                {s.itemType === 'yes_no' ? 'Yes/No' :
                                 s.itemType === 'dropdown' ? 'Dropdown' :
                                 s.itemType === 'checkbox' ? 'Checkbox' :
                                 s.itemType === 'short_text' ? 'Short Text' :
                                 s.itemType === 'observation' ? 'Observation' : 'Task'}
                              </span>
                            </div>
                            {s.itemType === 'observation' && s.allowedValues && s.allowedValues.length > 0 && (
                              <p className="text-xs text-gray-500 mt-0.5">
                                Carer picks one of: {s.allowedValues.join(', ')}
                              </p>
                            )}

                            {d.selected && (
                              <div className="mt-2 space-y-2">
                                <textarea
                                  value={d.supportNote}
                                  onChange={e => updateField(s.id, { supportNote: e.target.value })}
                                  rows={2}
                                  placeholder='Support note — the "how". e.g. "Use shower chair. Prefers warm water."'
                                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                                />
                                <div className="flex items-center gap-2">
                                  <label className="text-xs text-gray-600">Frequency</label>
                                  <select
                                    value={d.frequency}
                                    onChange={e => updateField(s.id, { frequency: e.target.value })}
                                    className="border border-gray-300 rounded-md px-2 py-1 text-sm"
                                  >
                                    {FREQUENCY_OPTIONS.map(o => (
                                      <option key={o.value} value={o.value}>{o.label}</option>
                                    ))}
                                  </select>
                                </div>
                              </div>
                            )}
                          </div>
                        </label>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          );
        })}
      </div>

      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 flex justify-end gap-3 shadow-lg">
        <button
          onClick={() => navigate('/residents')}
          className="px-4 py-2 text-sm text-gray-700 hover:text-gray-900"
        >
          Cancel
        </button>
        <button
          onClick={save}
          disabled={saving}
          className="px-5 py-2 bg-blue-600 text-white rounded-md text-sm font-semibold hover:bg-blue-700 disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save Care Plan'}
        </button>
      </div>
    </div>
  );
};

export default ResidentCarePlanEditor;
